import { lerpPosition } from '@/lib/lerpPosition';

const ms = (p) => new Date(p.fixTime).getTime();

/**
 * Interpolated position of a track at absolute time `t` (ms), or null when
 * `t` is outside [first.fixTime, last.fixTime]. Binary-searches the bracket.
 */
export function sampleTrackAtTime(positions, t) {
  if (!positions || positions.length === 0) return null;
  const first = ms(positions[0]);
  const last = ms(positions[positions.length - 1]);
  if (t < first || t > last) return null;
  if (positions.length === 1) return { ...positions[0] };

  let lo = 0, hi = positions.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ms(positions[mid]) <= t) lo = mid; else hi = mid - 1;
  }
  if (lo >= positions.length - 1) return { ...positions[positions.length - 1] };
  const a = positions[lo], b = positions[lo + 1];
  const span = ms(b) - ms(a);
  const frac = span > 0 ? (t - ms(a)) / span : 0;
  return { ...lerpPosition(a, b, frac), course: a.course, attributes: a.attributes };
}

const SPEED_KMH = (p) => (p.speed || 0) * 1.852;

/**
 * Activity runs for the timeline lane. Each run: { state, startMs, endMs }.
 * state: 'stopped' (<1 km/h), 'idle' (<5 km/h), else 'moving'.
 */
export function buildActivitySegments(positions) {
  if (!positions || positions.length < 2) return [];
  const classify = (p) => {
    const k = SPEED_KMH(p);
    if (k < 1) return 'stopped';
    if (k < 5) return 'idle';
    return 'moving';
  };
  const segs = [];
  let state = classify(positions[0]);
  let startMs = ms(positions[0]);
  for (let i = 1; i < positions.length; i++) {
    const s = classify(positions[i]);
    if (s !== state) {
      segs.push({ state, startMs, endMs: ms(positions[i]) });
      state = s;
      startMs = ms(positions[i]);
    }
  }
  segs.push({ state, startMs, endMs: ms(positions[positions.length - 1]) });
  return segs;
}

const R = 6371000; // earth radius m
export function haversine(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude), lat2 = toRad(b.latitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** { distanceM, durationMs, avgSpeedKmh, movingMs }. */
export function computeTrackStats(positions) {
  if (!positions || positions.length < 2) {
    return { distanceM: 0, durationMs: 0, avgSpeedKmh: 0, movingMs: 0 };
  }
  let distanceM = 0, movingMs = 0;
  for (let i = 1; i < positions.length; i++) {
    distanceM += haversine(positions[i - 1], positions[i]);
    if (SPEED_KMH(positions[i]) >= 1) movingMs += ms(positions[i]) - ms(positions[i - 1]);
  }
  const durationMs = ms(positions[positions.length - 1]) - ms(positions[0]);
  const avgSpeedKmh = movingMs > 0 ? (distanceM / 1000) / (movingMs / 3600000) : 0;
  return { distanceM, durationMs, avgSpeedKmh, movingMs };
}
